'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface ValueProposition {
  id: number;
  title: string;
  subtitle: string;
  highlight: string;
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const valuePropositions: ValueProposition[] = [
    {
      id: 2,
      title: "Technology. Talent. Intelligence.",
      subtitle: "Delivered.",
      highlight: "Comprehensive ecosystem for digital transformation"
    },
    {
      id: 1,
      title: "We Don't Just Advise.",
      subtitle: "We Deliver.",
      highlight: "Execution-first, outcome-driven approach"
    },
    {
      id: 3,
      title: "Your Trusted Partner",
      subtitle: "Not Just a Vendor.",
      highlight: "Partnership-driven, results-obsessed"
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % valuePropositions.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, valuePropositions.length]);

  const handleDotClick = (index: number) => {
    if (!isHydrated) return;
    setCurrentIndex(index);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-primary-foreground text-white overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-conversion rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="min-h-[200px] sm:min-h-[180px] md:min-h-[160px]">
              {isHydrated ? (
                <div className="transition-opacity duration-500">
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold leading-tight mb-4">
                    {valuePropositions[currentIndex].title}
                    <br />
                    <span className="text-accent">{valuePropositions[currentIndex].subtitle}</span>
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-200 font-body">
                    {valuePropositions[currentIndex].highlight}
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold leading-tight mb-4">
                    We Don't Just Advise.
                    <br />
                    <span className="text-accent">We Deliver.</span>
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-200 font-body">
                    Execution-first, outcome-driven approach
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-conversion text-conversion-foreground font-headline font-semibold text-base rounded-md hover:bg-opacity-90 shadow-cta hover:shadow-partnership transition-all duration-base transform hover:-translate-y-0.5"
              >
                Schedule Consultation
                <Icon name="ArrowRightIcon" size={20} variant="outline" className="ml-2" />
              </Link>
              <Link
                href="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-white bg-opacity-10 backdrop-blur-sm text-white font-headline font-semibold text-base rounded-md hover:bg-opacity-20 border border-white border-opacity-30 transition-all duration-base"
              >
                Explore Services
              </Link>
            </div>

            <div className="flex items-center space-x-2">
              {valuePropositions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`h-2 rounded-full transition-all duration-base ${
                    isHydrated && currentIndex === index
                      ? 'w-8 bg-accent' :'w-2 bg-white bg-opacity-50 hover:bg-opacity-75'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-accent to-conversion opacity-20 rounded-2xl transform rotate-3"></div>
              <div className="relative bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-20 shadow-partnership">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      <Icon name="CpuChipIcon" size={24} variant="solid" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold mb-2">AI Agents Development</h3>
                      <p className="text-sm text-gray-200 font-body">Custom intelligent automation solutions</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                      <Icon name="CodeBracketIcon" size={24} variant="solid" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold mb-2">Custom Web App Development</h3>
                      <p className="text-sm text-gray-200 font-body">Full-stack development with modern frameworks</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-conversion rounded-lg flex items-center justify-center">
                      <Icon name="UsersIcon" size={24} variant="solid" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold mb-2">Contract Staffing</h3>
                      <p className="text-sm text-gray-200 font-body">Skilled technology professionals on demand</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      <Icon name="CircleStackIcon" size={24} variant="solid" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold mb-2">Data Engineering Solutions</h3>
                      <p className="text-sm text-gray-200 font-body">Scalable data infrastructure solutions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;